import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppRoutes from 'constants/AppRoutes';
import { DATA_SET_PAGINATION_COUNT } from 'constants/dataSets';
import { KEY_PERMISSIONS, MATERIALITY_ASSESSMENT_STATUS, TASK_MANAGEMENT_PERMISSIONS } from 'constants/general';
import { SECTION_TYPE, STATUS_SECTIONS } from 'constants/materialAssessment';
import useNavigateStakeholders from 'hooks/useNavigateStakeholders';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import i18n from 'i18n';
import { useGetNaceCodeTreeQuery } from 'store/slices/dataSets/apis/dataSetsApi';
import { useUpdateSectionMAMutation } from 'store/slices/layoutsMA/apis/materialAssessmentApi';
import {
  useDeleteVCMaterialityStakeholderMutation,
  useGetSingleMAInitiateQuery,
  useGetStakeholdersCustomersListQuery, useSaveSelectedCustomerMutation,
} from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import { useLazyGetVCCustomersQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';

import MaterialityAssessmentCustomerView from './MaterialityAssessmentCustomerView';

import type ICustomerItem from 'store/slices/dataSets/interfaces/ICustomerItem';
import type ISelectedSuppliers from 'store/slices/materialAssessment/interfaces/ISelectedSuppliers';
import type IStakeholderCustomerItem from 'store/slices/materialAssessment/interfaces/IStakeholderCustomerItem';

function MaterialityAssessmentCustomer() {
  const navigate = useNavigate();
  const { recordId = '', vcmId = 0 } = useParams();
  const location = useLocation();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [selectedItems, setSelectedItems] = useState<number[]>([]);

  const [vcCustomers, setVCCustomers] = useState<ICustomerItem[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMoreVCCustomers, setHasMoreVCCustomers] = useState(false);
  const [offsetMain, setOffsetMain] = useState(0);
  const [selectedVCCustomers, setSelectedVCCustomers] = useState<ISelectedSuppliers[]>([]);
  const [listCustomers, setListCustomers] = useState<IStakeholderCustomerItem[]>([]);

  const [search, setSearch] = useState('');

  const [saveSelectedSupplier] = useSaveSelectedCustomerMutation();
  const [deleteMaterialityStakeholder] = useDeleteVCMaterialityStakeholderMutation();
  const { navigateToStakeholder } = useNavigateStakeholders(recordId, Number(vcmId));
  const { data: singleMA, isFetching: isLoadingSingleMA } = useGetSingleMAInitiateQuery({ id: recordId });
  const { data: naceCodeTreeData = { data: {} }, isLoading: isGetNaceCodeTreeRecords } = useGetNaceCodeTreeQuery();
  const [updateSection] = useUpdateSectionMAMutation();
  const { data: userData, isLoading: isUserLoading } = useUserMeQuery();
  const {
    access,
  } = usePillarModulePermission({
    pillarModuleKey: KEY_PERMISSIONS.MATERIALITY_ASSESSMENT_MANAGEMENT,
    userId: userData?.data?.id,
  });

  const isViewOnly = access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY
  || access === TASK_MANAGEMENT_PERMISSIONS.SIGN_OFF
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW;

  const { data: customersList, isFetching: isFetchingCustomersList } = useGetStakeholdersCustomersListQuery({
    maId: recordId,
    offset: offsetMain,
    valueChainMapId: Number(vcmId),
  }, {
    skip: !vcmId,
    refetchOnMountOrArgChange: true,
  });

  const hasMore = customersList?.data?.meta.hasNext || false;

  const [getVCCustomers] = useLazyGetVCCustomersQuery();

  const loadMoreCustomers = () => {
    if (customersList?.data.meta.hasNext) {
      setOffsetMain((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  const loadMoreVCCustomers = () => {
    if (hasMoreVCCustomers) {
      setOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  useEffect(() => {
    if (customersList && customersList?.data?.items.length > 0) {
      setListCustomers((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const newItems = customersList?.data?.items
          .filter((item) => !existingIds.has(item.id))
          .map((item) => ({ ...item, custom: true }));
        return [...prev, ...newItems];
      });
    }
  }, [customersList]);

  useEffect(() => {
    if (Number(vcmId) !== 0) {
      getVCCustomers({ valueChainMapId: Number(vcmId), offset, keyword: search }).then((response) => {
        const responseData = response?.data?.data?.items;
        if (responseData) {
          if (offset === 0) {
            setVCCustomers(responseData);
          } else {
            setVCCustomers((prev) => [...prev, ...responseData]);
          }
          setHasMoreVCCustomers(response?.data?.data?.meta?.hasNext || false);
        }
      });
    }
  }, [getVCCustomers, offset, search, vcmId, isOpenModal]);

  const handleUpdateSection = () => {
    updateSection({
      maId: recordId,
      type: SECTION_TYPE.STAKEHOLDER_CUSTOMERS,
      valueChainMapId: Number(vcmId),
      data: {
        isActive: true,
        status: listCustomers.length > 0 ? STATUS_SECTIONS.COMPLETED : STATUS_SECTIONS.IN_PROGRESS,
      },
    });
  };

  const goToNextMaterialityAssessment = () => {
    if (isViewOnly) {
      navigateToStakeholder(location.pathname.split('/')[3]);
      return;
    }
    handleUpdateSection();
    navigateToStakeholder(location.pathname.split('/')[3]);
  };

  const goBack = () => {
    navigate(AppRoutes.materialityAssessmentStakeholders.replace(':recordId', recordId));
  };

  const addStakeholder = () => {
    if (isViewOnly) {
      return;
    }
    setIsOpenModal(!isOpenModal);
    setSelectedItems([]);
  };
  const updateSelectedVCCustomers = (newSelectedItems: number[]) => {
    setSelectedVCCustomers((prev) => {
      if (newSelectedItems.length === 0) {
        return prev.filter((item) => item.vcmId !== Number(vcmId));
      }
      const data = {
        vcmId: Number(vcmId),
        recordIds: newSelectedItems,
      };
      const exists = prev.some((item) => item.vcmId === data.vcmId);
      if (exists) {
        return prev.map((item) => (item.vcmId === data.vcmId ? data : item));
      }
      return [...prev, data];
    });
  };

  const onSelectedItems = (id: number) => {
    setSelectedItems((prevSelectedItems) => {
      const newSelectedItems = prevSelectedItems.includes(id)
        ? prevSelectedItems.filter((item) => item !== id)
        : [...prevSelectedItems, id];
      updateSelectedVCCustomers(newSelectedItems);

      return newSelectedItems;
    });
  };

  const addCustomer = () => {
    if (isViewOnly) {
      return;
    }
    if (recordId && selectedVCCustomers.length) {
      saveSelectedSupplier({
        maId: Number(recordId),
        data: selectedVCCustomers,
        vcmId: Number(vcmId),
      }).then(() => {
        toast.success(i18n.t('notifications.saveSelectedCustomers'));
      }).catch(() => {
        toast.error(i18n.t('notifications.errorSaveSelectedCustomers'));
      });
    }
    setIsOpenModal(!isOpenModal);
    setOffsetMain(0);
  };

  const closeModal = () => {
    setIsOpenModal(false);
    setOffset(0);
    setVCCustomers([]);
    setSelectedItems([]);
  };

  const onOpenCreateModal = () => {
    if (isViewOnly) {
      return;
    }
    setIsCreateModalOpen(!isCreateModalOpen);
    setIsOpenModal(false);
  };

  const onDeleteItem = (id: number) => {
    if (isViewOnly) {
      return;
    }
    deleteMaterialityStakeholder({ id, maId: recordId }).then(() => {
      toast.success(i18n.t('notifications.deleteSelectedCustomers'));
      setListCustomers((prev) => {
        const filtered = prev.filter((item) => item.id !== id);
        if (filtered.length === 0) {
          updateSection({
            maId: recordId,
            type: SECTION_TYPE.STAKEHOLDER_CUSTOMERS,
            valueChainMapId: Number(vcmId),
            data: {
              isActive: true,
              status: STATUS_SECTIONS.IN_PROGRESS,
            },
          });
        }
        return filtered;
      });
    }).catch(() => {
      toast.error(i18n.t('notifications.errorDeleteSelectedCustomers'));
    });
  };

  const onSearchChange = (value: string) => {
    setSearch(value);
  };

  const setUnSelectedItems = () => {
    setSelectedItems([]);
  };

  const loading = isFetchingCustomersList || isGetNaceCodeTreeRecords || isLoadingSingleMA || isUserLoading;

  const vcmName = singleMA?.data?.valueChainMaps?.find((item) => item.id === Number(vcmId))?.name;

  return (
    <MaterialityAssessmentCustomerView
      isEmptyList={selectedVCCustomers?.length === 0}
      isEmptyMainList={customersList?.data?.items?.length === 0}
      loading={loading}
      goToNextMaterialityAssessment={goToNextMaterialityAssessment}
      goBack={goBack}
      addStakeholder={addStakeholder}
      isOpenModal={isOpenModal}
      addCustomer={addCustomer}
      closeModal={closeModal}
      onOpenCreateModal={onOpenCreateModal}
      isCreateModalOpen={isCreateModalOpen}
      selectedItems={selectedItems}
      setSelectedItems={onSelectedItems}
      vcCustomers={vcCustomers}
      onDeleteItem={onDeleteItem}
      hasMore={hasMore}
      loadMoreCustomers={loadMoreCustomers}
      listCustomers={listCustomers || []}
      naceCodeTreeData={naceCodeTreeData}
      search={search}
      onSearchChange={onSearchChange}
      setUnSelectedItems={setUnSelectedItems}
      vcmId={Number(vcmId)}
      vcmName={vcmName || ''}
      loadMoreVCCustomers={loadMoreVCCustomers}
      hasMoreVCCustomers={hasMoreVCCustomers}
      isViewOnly={isViewOnly}
    />
  );
}

export default MaterialityAssessmentCustomer;
